import {
  DateTimeFormatter,
  getCurrentUserHook,
  withErrorBoundary,
  ALL_MODULES,
  getDownloadURL,
  AttachmentPreviewer,
  useIsAuthorisedHook,
  BackgroundIcon,
  generateString,
  ApiProgressLoader,
  getTranslatedText,
  AppContext,
  TICKET_SOURCE
} from '@nile/components';
import {
  Avatar,
  TextStyle,
  Popover,
  DisplayText,
  Card,
  Link
} from '@superops/ui-design-system';
import React, { useCallback, useContext, useState, useEffect } from 'react';

import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import OriginalMessage from '@nile/mangifera/src/Components/Sheets/OriginalMessage';
import SplitSheet from '@nile/mangifera/src/Components/Sheets/SplitSheet';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import dompurify from 'dompurify';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { SPLIT_TICKET, RESEND_TICKET_CONVERSATION } from '@nile/graphql/src';
import { useMutation } from '@apollo/client';
import EmailFailureStatus from '@nile/mangifera/src/Components/Chat/EmailFailureState.js';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage, useIntl } from 'react-intl';
import Collapse from 'react-collapse';
import Recepients from '@nile/mangifera/src/Components/Chat/Recepients';
import { Tooltip } from 'react-clone-tippy';
import { isEqual } from 'lodash';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { withRouter } from 'react-router-dom';

let showEmailSentTimer;

const ConversationBubbleNew = (props) => {
  const domain = window.location.host;

  const { data, ticketId, showEmailSent, readOnly, ticketSource } = props;
  const currentUser = getCurrentUserHook();
  const intl = useIntl();
  const isMyChat = currentUser?.userId === data?.user?.userId;

  const getInitial = useCallback(() => {
    if (data?.user?.firstName) {
      return data.user?.firstName?.[0];
    } else if (data?.user?.email) {
      return data?.user.email?.[0];
    }
  }, [data]);

  const getAddedUser = useCallback(() => {
    if (data?.user?.firstName) {
      return `${data?.user?.firstName || ''} ${data?.user?.lastName || ''}`;
    } else if (data?.user?.email) {
      return data?.user?.email;
    } else if (data?.messageType === 'REQ_NOTIFICATION') {
      return getTranslatedText(intl, 'common.user.system');
    }
  }, [data]);

  const initial = getInitial();
  const messageType = data?.conversation?.type;

  const addedUser = getAddedUser();

  const [actionPopoverActive, setActionPopoverActive] = useState(false);

  const tabContext = useContext(TicketContext);

  const isTicketSplitAuthorised = useIsAuthorisedHook('splitTicket.allow');

  // Attachment preview functions
  const attachments = data?.conversation?.attachments || [];
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const [formatterAttachment, setFormatterAttachment] = useState([]);

  const [conversationStatus, setConversationStatus] = useState(null);

  const updateConvoStatus = (outgoingMailStatus) => {
    if (!isEqual(conversationStatus, outgoingMailStatus)) {
      if (outgoingMailStatus === 'SUCCESS') {
        setConversationStatus(null);
      } else {
        setConversationStatus(outgoingMailStatus);
      }
    }
  };

  useEffect(() => {
    if (showEmailSent) {
      if (showEmailSentTimer) clearTimeout(showEmailSentTimer);
      showEmailSentTimer = setTimeout(() => {
        updateConvoStatus('SUCCESS');
      }, 900);
    }
  }, [showEmailSent]);

  const appContext = useContext(AppContext);

  const [
    resendTicketConversation,
    { loading: resendTicketConversationLoading }
  ] = useMutation(RESEND_TICKET_CONVERSATION, {
    onCompleted: (res) => {
      if (res?.resendTicketConversation?.outgoingMailStatus)
        updateConvoStatus(res?.resendTicketConversation?.outgoingMailStatus);
    }
  });

  useEffect(() => {
    if (data?.conversation?.outgoingMailStatus)
      updateConvoStatus(data?.conversation?.outgoingMailStatus);
  }, [data?.conversation?.outgoingMailStatus]);

  const getAttachmentURL = (item) => {
    const module = ALL_MODULES.ticketConversationAttachment;

    const details = {
      fileName: item?.fileName,
      conversationId: data?.conversation?.conversationId,
      ticketId: ticketId
    };

    const url = getDownloadURL({ module, details });
    return url;
  };

  function formatAttachmentForPreview(attachments, cb) {
    const formattedItems = attachments.map((item) => {
      if (item)
        return {
          name: item.originalFileName,
          url: getAttachmentURL(item),
          type: item.fileName.substr(
            item.fileName.lastIndexOf('.') + 1,
            item.fileName.length
          )
        };
    });
    setFormatterAttachment(formattedItems);
    cb();
  }

  function openAttachmentPreview(index, attachments, item) {
    if (attachments) {
      formatAttachmentForPreview(attachments, () => {
        setShowAttachmentPreview({
          ...showAttachmentPreview,
          show: true,
          index
        });
      });
    }
  }

  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  //END: Attachment preview functions

  const togglePopOver = useCallback(() => {
    setActionPopoverActive(!actionPopoverActive);
  }, [actionPopoverActive]);

  const htmlData = data?.conversation?.content;

  let htmlToDisplay = dompurifier(htmlData, {
    FORBID_TAGS: ['style']
  });

  htmlToDisplay = htmlToDisplay.replaceAll('<img', "<img loading='lazy'");

  // SPLIT TICKET OPERATIONS
  const [splitTickets] = useMutation(SPLIT_TICKET, {
    onCompleted(res) {
      console.log(res);
    },
    onError(res) {
      console.log(res);
    }
  });

  const openSplitTicket = useCallback((res) => {
    const ticketData = res.data.splitTicket;
    const ticketId = ticketData?.ticketId;
    tabContext.openTab({
      module: 'TICKET',
      ticketId: ticketId,
      data: ticketData
    });
  }, []);

  const splitTicket = useCallback(
    (subject, copyFields) => {
      splitTickets({
        variables: {
          ticketId: ticketId,
          conversationId: data?.conversation?.conversationId,
          subject,
          copyFields
        }
      }).then((res) => {
        openSplitTicket(res);
      });
    },
    [data, ticketId]
  );

  const selectTicket = useCallback((value) => {
    setSelected(value);
  }, []);

  // END SPLIT TICKET OPERATIONS

  const getConversationIcon = (type) => {
    switch (type) {
      case 'DESCRIPTION':
        return 'Reply';
      case 'VENDOR_REPLY':
        return 'helpcenter';
      case 'CHAT':
        return 'Chat Initiated by';
      default:
        return 'onboardingNotificationTemplate';
    }
  };

  const getHeaderMessageKey = (type) => {
    switch (type) {
      case 'REQ_NOTIFICATION':
        return 'ticket.details.emailNotificationSent.label';
      case 'DESCRIPTION':
        return 'ticket.details.postedBy';
      case 'CHAT':
        return 'IntercomChat';
      default:
        return 'ticket.details.chat.reply';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <SVGIcon name="SuccessCircle" />;
      case 'FAILURE':
        return <SVGIcon name="WarningOutline" color="red" />;
      default:
        return <ApiProgressLoader isLoading />;
    }
  };

  const getStatusMessageKey = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'ticket.details.emailNotification.status.success';
      case 'FAILURE':
        return 'ticket.details.emailNotification.status.notDelivered';
      default:
        return 'ticket.details.emailNotification.status.progress';
    }
  };

  function resendConversation() {
    resendTicketConversation({
      variables: {
        ticketId: ticketId,
        input: {
          conversationId: data?.conversation?.conversationId
        }
      }
    });
  }

  const [isBubbleOpen, setIsBubbleOpen] = useState(false);

  const toggleIsOpen = useCallback(() => {
    setIsBubbleOpen(!isBubbleOpen);
  }, [isBubbleOpen]);

  const handleEscPress = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e?.keyCode === 27 || e?.which === 27 || e?.key === 'Escape') {
      if (
        window.getComputedStyle(document.querySelector(`.modal-inline-images`))
          .display === 'flex'
      ) {
        document.querySelector(`.modal-inline-images`).style.display = 'none';
      }
      window.removeEventListener('keyup', handleEscPress);
    }
  };

  const imageDownloader = (e) => {
    // window.open(e.target.currentSrc, '_blank').focus();
    const modal = document.querySelector(`.modal-inline-images`);

    const modalImg = document.querySelector(`.modal-content-inline-images`);

    modal.style.display = 'flex';
    modalImg.src = e.target.currentSrc;
    window.addEventListener('keyup', handleEscPress);
  };

  const randomId = generateString(5) || 'abcde';

  useEffect(() => {
    if (
      document.querySelector(`.${randomId}`)?.querySelectorAll('img')?.length
    ) {
      document
        ?.querySelector(`.${randomId}`)
        ?.querySelectorAll('img')
        ?.forEach((img) => {
          img.classList.add('cursor-zoom-in', 'mw100');
          img.addEventListener('click', imageDownloader);
        });
      return () => {
        document
          ?.querySelector(`.${randomId}`)
          ?.querySelectorAll('img')
          ?.forEach((img) => {
            img.classList.remove('cursor-zoom-in');
            img.removeEventListener('click', imageDownloader);
          });
      };
    }
  }, []);

  const flags = useFlags();

  const conversationBubbleWrapperClassNames = [
    'flex g10 bubble-wrapper',
    isMyChat && 'frow-reverse',
    'px10'
  ]
    .filter(Boolean)
    .join(' ');

  const isStatusPending =
    conversationStatus && conversationStatus !== 'SUCCESS';

  const isEmailNotification = !initial && messageType !== 'DESCRIPTION';

  const bubbleOuterClassNames = ['w100'].filter(Boolean).join(' ');

  const bubblePadding = isEmailNotification
    ? 'pt20 pb20 px10'
    : 'pt20 pb20 px10';

  const conversationBubbleClassNames = ['word-break br20', bubblePadding]
    .filter(Boolean)
    .join(' ');

  const headerClassNames = ['bubble-header fsize12 flex actr fx-sb fg1']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={conversationBubbleWrapperClassNames}>
      <div id="conversation-bubble-outer" className={bubbleOuterClassNames}>
        <Card
          variant={isStatusPending ? 'none' : isMyChat ? 'discussion' : 'card'}
          padding="sizeExtraSmall"
          border={isStatusPending ? 'none' : 'none'}
        >
          <div
            id="conversation-bubble-inner"
            className={conversationBubbleClassNames}
          >
            <div className="flex g10 align-items-center">
              <div className="mr8">
                <Avatar
                  initials={initial}
                  size={'extraSmall'}
                  source={
                    !initial && messageType !== 'DESCRIPTION'
                      ? `${envConfig.imageRoot}svg/notification.svg`
                      : ''
                  }
                />
              </div>
              <div className="fw">
                <div className={headerClassNames}>
                  <div className="added-by flex g5 actr">
                    {!data?.user && (
                      <div className="flex mr-5">
                        <SVGIcon
                          name={getConversationIcon(messageType)}
                          size="sm"
                        />
                      </div>
                    )}
                    <div className="flex g3">
                      <div>
                        <FormattedMessage
                          id={getHeaderMessageKey(messageType)}
                        />
                      </div>

                      {addedUser && (
                        <div>
                          <TextStyle variation="strong">{addedUser}</TextStyle>
                        </div>
                      )}
                    </div>

                    {!data.user && (
                      <div onClick={toggleIsOpen} className="flex pointer ml4">
                        <SVGIcon name="MoreHorizontal" />
                      </div>
                    )}

                    <Tooltip
                      html={
                        <>
                          <Recepients
                            message={{
                              toUsers: data?.conversation?.toUsers,
                              ccUsers: data?.conversation?.ccUsers,
                              bccUsers: data?.conversation?.bccUsers
                            }}
                          />
                        </>
                      }
                      delay={300}
                      duration={250}
                      position="bottom-start"
                      inertia
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                      theme="super-light"
                      className="flex"
                    >
                      <div className="flex center ml-5 mb-2">
                        <SVGIcon name="DropSheet" size="sm" />
                      </div>
                    </Tooltip>
                  </div>

                  <div
                    key={conversationStatus}
                    className="flex g3 right-container actr"
                  >
                    {conversationStatus || showEmailSent ? (
                      <div className="fx actr g5">
                        {resendTicketConversationLoading ? (
                          <ApiProgressLoader isLoading />
                        ) : (
                          getStatusIcon(
                            showEmailSent ? 'SUCCESS' : conversationStatus
                          )
                        )}
                        {conversationStatus === 'FAILURE' &&
                        !resendTicketConversationLoading ? (
                          <Tooltip
                            html={
                              <>
                                {data?.conversation
                                  ?.outgoingMailFailureReason && (
                                  <div className="p10 rounded-4px fx fcol g5 bshadow">
                                    <div className="fx fcol g3">
                                      <DisplayText size="medium" weight="Bold">
                                        {getTranslatedText(
                                          intl,
                                          'common.error'
                                        )}
                                      </DisplayText>
                                      <DisplayText
                                        weight="Regular"
                                        size="small"
                                      >
                                        <TextStyle variation="subdued">
                                          <DateTimeFormatter
                                            type="dateTime"
                                            date={data?.time}
                                            company={appContext.company}
                                          />
                                        </TextStyle>
                                      </DisplayText>
                                    </div>
                                    <DisplayText>
                                      {getTranslatedText(
                                        intl,
                                        `app.errorCode.${data?.conversation?.outgoingMailFailureReason?.errorCode}`
                                      )}
                                    </DisplayText>
                                  </div>
                                )}
                              </>
                            }
                            delay={300}
                            duration={250}
                            position="bottom-start"
                            inertia
                            trigger="mouseenter"
                            animation="shift"
                            animateFill={false}
                            theme="super-light"
                          >
                            <TextStyle variation="subdued">
                              <FormattedMessage
                                id={
                                  resendTicketConversationLoading
                                    ? 'ticket.details.emailNotification.status.progress'
                                    : getStatusMessageKey(
                                        showEmailSent
                                          ? 'SUCCESS'
                                          : conversationStatus
                                      )
                                }
                              />
                            </TextStyle>
                          </Tooltip>
                        ) : (
                          <TextStyle variation="subdued">
                            <FormattedMessage
                              id={
                                resendTicketConversationLoading
                                  ? 'ticket.details.emailNotification.status.progress'
                                  : getStatusMessageKey(
                                      showEmailSent
                                        ? 'SUCCESS'
                                        : conversationStatus
                                    )
                              }
                            />
                          </TextStyle>
                        )}
                        {!resendTicketConversationLoading &&
                          isMyChat &&
                          conversationStatus === 'FAILURE' && (
                            <div
                              onClick={resendConversation}
                              className="linkText"
                            >
                              <FormattedMessage id="common.retry" />
                            </div>
                          )}
                      </div>
                    ) : (
                      <TextStyle
                        variation="subdued"
                        title={
                          <DateTimeFormatter
                            type="dateTime"
                            date={data?.time}
                          />
                        }
                      >
                        <div className="flex">
                          <DateTimeFormatter
                            type="dateTime"
                            date={data?.time}
                          />
                        </div>
                      </TextStyle>
                    )}

                    <div className="email-failure-wrapper">
                      <EmailFailureStatus
                        ccUsers={data?.conversation?.ccUsers}
                        toUsers={data?.conversation?.toUsers}
                        bccUsers={data?.conversation?.toUsers}
                        size="sm"
                      />
                    </div>

                    {!conversationStatus &&
                      !readOnly &&
                      ticketSource !== TICKET_SOURCE.Communication && (
                        <span
                          className="pl-4 pointer flex actr"
                          id="bubble-actions"
                        >
                          <Popover
                            activator={
                              <div onClick={togglePopOver} className="ptr flex">
                                <SVGIcon name="Menu" size="sm" />
                              </div>
                            }
                            active={actionPopoverActive}
                            preferredAlignment="right"
                            onClose={togglePopOver}
                          >
                            <div
                              className="flex col g15 p10"
                              id="action-wrapper"
                            >
                              {isTicketSplitAuthorised && (
                                <SplitSheet
                                  splitTicket={splitTicket}
                                  selectTicket={selectTicket}
                                />
                              )}
                              <OriginalMessage
                                message={{ ticketId: ticketId }}
                                fullHeight={!isTicketSplitAuthorised}
                              />
                            </div>
                          </Popover>
                        </span>
                      )}
                  </div>
                </div>

                <Collapse
                  isOpened={
                    data.user || messageType === 'DESCRIPTION' || isBubbleOpen
                  }
                >
                  <div className="w100">
                    <Card
                      noShadow
                      variant={
                        isStatusPending
                          ? 'none'
                          : messageType === 'REQ_NOTIFICATION'
                          ? 'secondaryCard'
                          : isMyChat
                          ? 'discussion'
                          : 'card'
                      }
                    >
                      <p
                        className={`message-content mb-10 mt-10 relative overflow-x-auto ${randomId} ${
                          messageType === 'REQ_NOTIFICATION' ? 'p10' : ''
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: htmlToDisplay
                        }}
                      ></p>
                      {data?.conversation?.minifiedContent && (
                        <a
                          href={`https://${domain}/#/minify/ticket/ticketconversation/${ticketId}/${data?.conversation?.conversationId}`}
                          target="_blank"
                        >
                          View All
                        </a>
                      )}

                      {attachments?.length > 0 && (
                        <div className="flex wrap mt-10 g10 attachments-container">
                          {attachments.map((item, index) => (
                            <AttachmentHolder
                              onClick={() =>
                                openAttachmentPreview(index, attachments, item)
                              }
                              fileName={item?.originalFileName || ''}
                              fontWeight="bolder"
                              padded
                            />
                          ))}
                        </div>
                      )}

                      {showAttachmentPreview.show && (
                        <AttachmentPreviewer
                          showPreviewer={showAttachmentPreview.show}
                          closePreviwer={closePreviwer}
                          list={formatterAttachment}
                          selectedIndex={showAttachmentPreview.index}
                        />
                      )}
                    </Card>
                  </div>
                </Collapse>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default withErrorBoundary(withRouter(ConversationBubbleNew));
